import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, Check, Loader2, Tag, X } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import TermsCheckbox from '../components/TermsCheckbox';
import { notifyError } from '../lib/notify';
import { trackViewBlueprint } from '../lib/analytics';
import { getStoredPromo } from '../lib/promo';
import { useAuth } from '../context/AuthContext';
import { PRICING_CONTENT, PRICING_TIER_ORDER } from '../constants/pricingContent';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'blueprint_intake_draft';
// iter 157 - Roles that already paid for a Blueprint. These users
// skip Step 2 ("Choose your plan") entirely.
const PAID_BLUEPRINT_ROLES = new Set(['blueprint_only', 'essentials', 'guided']);

const cities = ['Mexico City', 'Mérida', 'Tulum', 'San Miguel de Allende', 'Puerto Vallarta', 'Playa del Carmen', 'Not sure yet'];
const profiles = ['Remote worker / Digital nomad', 'Family with children', 'Retiree', 'Investor / Entrepreneur'];
const timelines = ['0–3 months', '3–6 months', '6–12 months', 'Just exploring'];
const carOptions = ['Yes - US plated', 'Yes - Mexican plated', 'No'];
const citizenships = ['US citizen only', 'Mexican citizen', 'Dual US-Mexican'];
const budgets = ['Under $2,000', '$2,000–$3,500', '$3,500–$5,000', '$5,000+'];
// iter 181 - household composition. Powers the Blueprint's per-applicant
// vs per-household visa math + household-scoped cost estimates.
const householdStatuses = ['Single (just me)', 'Couple (2 adults)', 'Family (adults + children)'];

const initialFormState = {
  city: '',
  // iter 101 - optional backup cities. Empty = focus entirely on primary
  // city per the iter 97 single-city rule.
  backup_city_1: '', backup_city_2: '',
  profile: '', timeline: '', children: '', children_details: '',
  pets: '', pets_details: '', car: '', citizenship: '', budget: '', name: '', email: '',
  // iter 181 - household fields fed to the Blueprint prompt so financial
  // requirements can be labeled per-applicant AND explicitly rolled up to
  // the applicant's household size.
  household_status: '',            // Single / Couple / Family
  dependent_adults: '',            // spouse or additional adult applicants
  dependent_children: '',          // children under 18 in the intake
};

// iter 176 - tier copy (name, tagline, description, bullets) now lives in
// constants/pricingContent.js. This file keeps only the intake-flow-specific
// bits: `popular` / `badge` markers used for card decoration.
const TIER_ORNAMENTS = {
  blueprint_only: {},
  essentials:     { popular: true, badge: 'Most Popular' },
  guided:         { badge: 'Advisor included' },
};

const tiers = PRICING_TIER_ORDER.map((id) => {
  const pc = PRICING_CONTENT[id];
  const orn = TIER_ORNAMENTS[id] || {};
  return {
    key: id,
    name: pc.name,
    price: pc.price,
    interval: pc.interval,
    tagline: pc.tagline,
    description: pc.description,
    features: pc.bullets,
    ...orn,
  };
});

const RadioGroup = ({ label, options, value, onChange, testId }) => (
  <div className="space-y-3">
    <label className="block text-[#0A1F2E] font-medium text-sm">{label}</label>
    <div className="grid sm:grid-cols-2 gap-2">
      {options.map((opt) => (
        <button
          key={opt}
          type="button"
          onClick={() => onChange(opt)}
          className={`text-left px-4 py-3 border text-sm transition-all rounded-lg ${value === opt ? 'border-[#E8A830] bg-[#E8A830]/10 text-[#0A1F2E]' : 'border-[#d5d0c8] text-[#5A6B73] hover:border-[#E8A830]/40'}`}
          data-testid={`${testId}-${opt.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
        >
          {opt}
        </button>
      ))}
    </div>
  </div>
);

export default function BlueprintIntake() {
  const navigate = useNavigate();
  const { user: authedUser } = useAuth();
  const [searchParams] = useSearchParams();
  // iter 157 - Paid users (came through the post-payment claim flow)
  // skip Step 2 ("Choose your plan") entirely. The intake submits
  // straight to the backend and we redirect to the portal dashboard
  // with a one-time confirmation message - no upsell screen.
  //
  // iter 186.b - Extended to also recognize the just-paid URL signal
  // (`?paid=1&email=<X>&tier=<current-tier>`) that BlueprintSuccess
  // emits when a first-time customer clicks "Start Your Blueprint".
  // At that point `authedUser` is still null (welcome-email loop
  // pending) but Stripe has already charged them - the intake must
  // NOT show the pricing card set again or a confused customer will
  // pay twice. Backend `/api/blueprint/intake` is a pure DB write and
  // `/api/blueprint/checkout` is only reachable from
  // `submitIntakeAsAnonymous`, so this client-side guard is the last
  // line of defense against the double-charge UX path.
  const _paidByUrl = (() => {
    if (searchParams.get('paid') !== '1') return false;
    const emailParam = (searchParams.get('email') || '').trim();
    const tierParam = (searchParams.get('tier') || '').toLowerCase();
    if (!emailParam || !tierParam) return false;
    return PAID_BLUEPRINT_ROLES.has(tierParam);
  })();
  const _paidByAuth = !!authedUser && PAID_BLUEPRINT_ROLES.has((authedUser.role || authedUser.tier || '').toLowerCase());
  const isPaidUser = _paidByAuth || _paidByUrl;
  // Which tier they paid for - needed by submitIntakeAsPaid to tell the
  // backend which product was purchased. Authed source of truth wins;
  // URL param is the fallback for the unauthenticated post-Stripe path.
  const paidTier = (
    (authedUser?.role || authedUser?.tier || '').toLowerCase()
      || (isPaidUser ? (searchParams.get('tier') || '').toLowerCase() : '')
  );
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initialFormState);
  // iter 144q - accept `?tier=blueprint_only|essentials|guided` so the
  // /start quiz funnel can land users on the correct pricing card.
  const _initialTier = (() => {
    if (typeof window === 'undefined') return 'essentials';
    const t = new URLSearchParams(window.location.search).get('tier') || '';
    return ['blueprint_only', 'essentials', 'guided'].includes(t) ? t : 'essentials';
  })();
  const [selectedTier, setSelectedTier] = useState(_initialTier);
  const [loading, setLoading] = useState(false);
  const [emailTracked, setEmailTracked] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoResult, setPromoResult] = useState(null);
  const [promoError, setPromoError] = useState('');
  const [showResumeBanner, setShowResumeBanner] = useState(false);
  const [savedProgress, setSavedProgress] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Check for saved progress on mount
  useEffect(() => {
    // iter 118 - fire GA4 `view_blueprint` once on page mount.
    try { trackViewBlueprint(); } catch { /* analytics is fire-and-forget */ }

    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Only show banner if there's meaningful progress
        const hasProgress = parsed.form && (parsed.form.city || parsed.form.email || parsed.form.name);
        if (hasProgress) {
          setSavedProgress(parsed);
          setShowResumeBanner(true);
        }
      } catch (e) {
        console.error('Error loading saved state:', e);
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }, []);

  // Save progress to localStorage on every change
  useEffect(() => {
    // Don't save if showing resume banner (haven't made a choice yet)
    if (showResumeBanner) return;
    
    const hasAnyData = form.city || form.profile || form.name || form.email;
    if (hasAnyData) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        form,
        step,
        selectedTier,
        timestamp: Date.now()
      }));
    }
  }, [form, step, selectedTier, showResumeBanner]);

  // Handle resume - restore saved progress
  const handleResume = () => {
    if (savedProgress) {
      setForm(savedProgress.form || initialFormState);
      setStep(savedProgress.step || 1);
      setSelectedTier(savedProgress.selectedTier || 'essentials');
    }
    setShowResumeBanner(false);
  };

  // Handle start over - clear saved progress
  const handleStartOver = () => {
    localStorage.removeItem(STORAGE_KEY);
    setForm(initialFormState);
    setStep(1);
    setSelectedTier('essentials');
    setShowResumeBanner(false);
    setSavedProgress(null);
  };

  // Pre-fill email from URL params (for resume links)
  useEffect(() => {
    if (showResumeBanner) return; // Don't pre-fill if showing banner
    const params = new URLSearchParams(window.location.search);
    const prefillEmail = params.get('email');
    if (prefillEmail) {
      setForm(prev => ({ ...prev, email: prefillEmail }));
    }
  }, [showResumeBanner]);

  const set = (field) => (val) => setForm((prev) => ({ ...prev, [field]: val }));

  // Capture email immediately on blur and start abandonment timer
  const handleEmailBlur = () => {
    const email = form.email.trim();
    if (email && email.includes('@') && !emailTracked) {
      setEmailTracked(true);
      fetch(`${API_URL}/api/automation/form-event`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ event: 'form_started', email, name: form.name || '', form_type: 'blueprint' })
      }).catch(() => {});
    }
  };

  const canProceed = form.city && form.profile && form.timeline && form.children && form.pets && form.car && form.citizenship && form.budget && form.household_status;

  // iter 157 - When a paid user finishes Step 1, POST the intake then
  // hand them off to the portal dashboard with a confirmation flag.
  // No Step 2, no pricing, no "Choose your plan".
  const submitIntakeAsPaid = async () => {
    if (loading) return;
    if (!canProceed) return;
    setLoading(true);
    try {
      await fetch(`${API_URL}/api/blueprint/intake`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          name: form.name || `${authedUser?.first_name || ''} ${authedUser?.last_name || ''}`.trim(),
          email: form.email || authedUser?.email || searchParams.get('email') || '',
          paid_user: true,
          // iter 186.b - fall back to the URL tier param for the
          // just-paid, not-yet-authenticated case. `paidTier` prefers
          // authedUser.role when available; URL is the safety net.
          paid_tier: paidTier,
        }),
      });
      try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
      // iter 186.b - Authed user → straight to their portal dashboard.
      // Unauthed just-paid user (paid=1 URL, no session yet) → sign-in
      // page with the iter 185 "just paid" nudge so they know to open
      // the welcome email + set their password. Prevents the previous
      // dead-end where they'd get bounced to /portal login with no
      // context after submitting.
      if (authedUser) {
        navigate('/portal/dashboard?blueprint=submitted', { replace: true });
      } else {
        navigate('/portal?just_paid=1&intake=submitted', { replace: true });
      }
    } catch {
      notifyError('Could not save your answers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyPromo = async () => {
    const code = promoCode.trim();
    if (!code) return;
    setPromoLoading(true);
    setPromoError('');
    setPromoResult(null);
    try {
      const res = await fetch(`${API_URL}/api/blueprint/validate-coupon`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, tier_key: selectedTier }),
      });
      const data = await res.json();
      if (res.ok && data.valid) {
        setPromoResult(data);
      } else {
        setPromoError(data.detail || 'Invalid coupon code');
      }
    } catch {
      setPromoError('Failed to validate code');
    } finally {
      setPromoLoading(false);
    }
  };

  const clearPromo = () => {
    setPromoCode('');
    setPromoResult(null);
    setPromoError('');
  };

  // Reset promo when tier changes
  const handleTierSelect = (tierKey) => {
    setSelectedTier(tierKey);
    if (promoResult) {
      clearPromo();
    }
  };

  const currentTier = tiers.find(t => t.key === selectedTier);
  const displayPrice = promoResult ? promoResult.new_price : (currentTier?.price || 0);

  const handleCheckout = async () => {
    if (!form.name || !form.email) return;
    setLoading(true);
    try {
      const intakeRes = await fetch(`${API_URL}/api/blueprint/intake`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          // iter 126 - pass nav-CTA attribution if set by Header click.
          signup_source: (() => {
            try { return sessionStorage.getItem('mundalo_signup_source') || null; } catch { return null; }
          })(),
        }),
      });
      const { intake_id } = await intakeRes.json();

      const checkoutBody = { intake_id, tier_key: selectedTier, email: form.email };
      if (promoResult) {
        checkoutBody.coupon_code = promoResult.code;
      } else {
        // iter 144n - pre-apply promo captured from the landing URL
        // (e.g., ?promo=LAUNCH25 on the homepage). Backend validates
        // against Stripe; invalid codes are silently dropped so this
        // never blocks checkout.
        const urlPromo = getStoredPromo();
        if (urlPromo) checkoutBody.coupon_code = urlPromo;
      }

      const checkoutRes = await fetch(`${API_URL}/api/blueprint/checkout`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(checkoutBody),
      });
      const { checkout_url } = await checkoutRes.json();
      if (checkout_url) {
        // Clear saved progress on successful submission
        localStorage.removeItem(STORAGE_KEY);
        
        if (form.email) {
          fetch(`${API_URL}/api/automation/form-submitted`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: form.email })
          }).catch(() => {});
        }
        window.location.href = checkout_url;
      }
    } catch {
      notifyError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      <SEO title="Your Personalized Mexico Relocation Blueprint" description="A 19-section personalized relocation plan built for your city, visa, budget, and timeline. Stop reading forums. Start packing. From $199." path="/relocation/blueprint" />
      <Header currentPage="relocation" />

      {/* Resume Banner */}
      {showResumeBanner && savedProgress && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="resume-banner-overlay">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-8" data-testid="resume-banner">
            <h3 className="text-xl text-[#0A1F2E] font-semibold mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              Welcome back!
            </h3>
            <p className="text-[#5A6B73] mb-6">
              You left off on step {savedProgress.step || 1}. Would you like to continue where you left off?
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleResume}
                className="flex-1 bg-[#E8A830] text-white py-3 px-4 rounded-lg font-medium hover:bg-[#a8511f] transition-all"
                data-testid="resume-continue-btn"
              >
                Continue
              </button>
              <button
                onClick={handleStartOver}
                className="flex-1 border border-[#d5d0c8] text-[#5A6B73] py-3 px-4 rounded-lg font-medium hover:bg-[#f5f2ed] transition-all"
                data-testid="resume-start-over-btn"
              >
                Start Over
              </button>
            </div>
          </div>
        </div>
      )}

      <section className="pt-[160px] pb-16 lg:pt-[180px] border-b border-[#e0dbd4]">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <p className="text-[#E8A830] text-sm font-medium tracking-widest uppercase mb-3">Relocation by Mundalo</p>
          <h1 className="text-4xl lg:text-5xl text-[#0A1F2E] mb-4" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="blueprint-hero-title">
            Your Personalized Relocation Blueprint
          </h1>
          <p className="text-lg text-[#5A6B73]">Answer a few questions. Get your custom relocation roadmap in 60 seconds.</p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          {step === 1 && (
            <div className="space-y-8" data-testid="intake-form">
              <div className="flex items-center gap-3 mb-8">
                <span className="w-8 h-8 flex items-center justify-center bg-[#E8A830] text-white text-sm font-bold rounded-full">1</span>
                <span className="text-[#0A1F2E] font-medium">Tell us about your move</span>
              </div>

              <div className="space-y-3">
                <label className="block text-[#0A1F2E] font-medium text-sm">Which city are you relocating to?</label>
                <select
                  value={form.city}
                  onChange={(e) => set('city')(e.target.value)}
                  className="w-full bg-white border border-[#d5d0c8] text-[#0A1F2E] px-4 py-3 text-sm focus:border-[#E8A830] outline-none rounded-lg"
                  data-testid="city-select"
                >
                  <option value="">Select a city</option>
                  {cities.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              {/* iter 101 - optional backup cities. Shown only after a primary
                  city is picked so the question makes sense. Both are
                  optional; empty values keep the Blueprint focused on the
                  primary city. */}
              {form.city && form.city !== 'Not sure yet' && (
                <div className="space-y-3 bg-white/5 border border-white/10 rounded-lg p-4">
                  <p className="text-[11px] uppercase tracking-[0.15em] text-[#E8A830] font-medium">
                    Considering other cities? (optional)
                  </p>
                  <p className="text-xs text-[#0A1F2E]/65 leading-relaxed">
                    Pick up to two backup options. We'll add a brief comparison
                    section to your Blueprint so you can validate your choice
                    - or pivot before you move.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <label htmlFor="backup_city_1" className="block text-[#0A1F2E] font-medium text-sm mb-1.5">
                        Second city of interest
                      </label>
                      <select
                        id="backup_city_1"
                        value={form.backup_city_1}
                        onChange={(e) => set('backup_city_1')(e.target.value)}
                        className="w-full bg-white border border-[#d5d0c8] text-[#0A1F2E] px-4 py-3 text-sm focus:border-[#E8A830] outline-none rounded-lg"
                        data-testid="backup-city-1-select"
                      >
                        <option value="">- None -</option>
                        {cities
                          .filter((c) => c !== 'Not sure yet' && c !== form.city && c !== form.backup_city_2)
                          .map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <label htmlFor="backup_city_2" className="block text-[#0A1F2E] font-medium text-sm mb-1.5">
                        Third city of interest
                      </label>
                      <select
                        id="backup_city_2"
                        value={form.backup_city_2}
                        onChange={(e) => set('backup_city_2')(e.target.value)}
                        className="w-full bg-white border border-[#d5d0c8] text-[#0A1F2E] px-4 py-3 text-sm focus:border-[#E8A830] outline-none rounded-lg"
                        data-testid="backup-city-2-select"
                        disabled={!form.backup_city_1}
                      >
                        <option value="">- None -</option>
                        {cities
                          .filter((c) => c !== 'Not sure yet' && c !== form.city && c !== form.backup_city_1)
                          .map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
              )}

              <RadioGroup label="What describes you best?" options={profiles} value={form.profile} onChange={set('profile')} testId="profile" />
              <RadioGroup label="What is your target move timeline?" options={timelines} value={form.timeline} onChange={set('timeline')} testId="timeline" />
              <RadioGroup label="Are you bringing children?" options={['Yes', 'No']} value={form.children} onChange={set('children')} testId="children" />
              {form.children === 'Yes' && (
                <div>
                  <label className="block text-[#0A1F2E] font-medium text-sm mb-2">How many and what ages?</label>
                  <input type="text" value={form.children_details} onChange={(e) => set('children_details')(e.target.value)} placeholder="e.g., 2 children - ages 5 and 8" className="w-full bg-white border border-[#d5d0c8] text-[#0A1F2E] px-4 py-3 text-sm focus:border-[#E8A830] outline-none rounded-lg" data-testid="children-details" />
                </div>
              )}

              {/* iter 181 - Household composition. Feeds the Blueprint's
                  per-applicant vs household visa math. Sits right after
                  the children question so all household fields cluster. */}
              <RadioGroup
                label="Who is applying with you?"
                options={householdStatuses}
                value={form.household_status}
                onChange={set('household_status')}
                testId="household-status"
              />
              {(form.household_status === 'Couple (2 adults)' || form.household_status === 'Family (adults + children)') && (
                <div className="grid sm:grid-cols-2 gap-4" data-testid="household-details">
                  <div>
                    <label className="block text-[#0A1F2E] font-medium text-sm mb-2">
                      # of dependent adults (spouse / partner / other adult applicants)
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={form.dependent_adults}
                      onChange={(e) => set('dependent_adults')(e.target.value)}
                      placeholder="e.g., 1"
                      className="w-full bg-white border border-[#d5d0c8] text-[#0A1F2E] px-4 py-3 text-sm focus:border-[#E8A830] outline-none rounded-lg"
                      data-testid="dependent-adults"
                    />
                  </div>
                  <div>
                    <label className="block text-[#0A1F2E] font-medium text-sm mb-2">
                      # of dependent children (under 18)
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={form.dependent_children}
                      onChange={(e) => set('dependent_children')(e.target.value)}
                      placeholder="e.g., 2"
                      className="w-full bg-white border border-[#d5d0c8] text-[#0A1F2E] px-4 py-3 text-sm focus:border-[#E8A830] outline-none rounded-lg"
                      data-testid="dependent-children"
                    />
                  </div>
                  <p className="sm:col-span-2 text-[11px] text-[#5A6B73] italic">
                    Visa financial requirements are stated per applicant. Bringing a spouse or dependents? Requirements increase for each dependent - amounts vary by consulate. Your Blueprint calculates this for your specific household.
                  </p>
                </div>
              )}
              <RadioGroup label="Are you bringing pets?" options={['Yes', 'No']} value={form.pets} onChange={set('pets')} testId="pets" />
              {form.pets === 'Yes' && (
                <div>
                  <label className="block text-[#0A1F2E] font-medium text-sm mb-2">What type?</label>
                  <input type="text" value={form.pets_details} onChange={(e) => set('pets_details')(e.target.value)} placeholder="e.g., 1 dog (golden retriever)" className="w-full bg-white border border-[#d5d0c8] text-[#0A1F2E] px-4 py-3 text-sm focus:border-[#E8A830] outline-none rounded-lg" data-testid="pets-details" />
                </div>
              )}
              <RadioGroup label="Are you bringing a car?" options={carOptions} value={form.car} onChange={set('car')} testId="car" />
              <RadioGroup label="Are you a US citizen, Mexican citizen, or dual?" options={citizenships} value={form.citizenship} onChange={set('citizenship')} testId="citizenship" />
              <RadioGroup label="What is your approximate monthly budget?" options={budgets} value={form.budget} onChange={set('budget')} testId="budget" />

              <button
                onClick={() => {
                  if (!canProceed) return;
                  // iter 157 - Paid users never see the pricing step.
                  if (isPaidUser) { submitIntakeAsPaid(); return; }
                  setStep(2);
                  window.scrollTo(0, 0);
                }}
                disabled={!canProceed || loading}
                className={`w-full py-4 text-sm font-medium flex items-center justify-center gap-2 transition-all rounded-lg ${canProceed && !loading ? 'bg-[#E8A830] text-white hover:bg-[#a8511f]' : 'bg-[#e0dbd4] text-[#5A6B73] cursor-not-allowed'}`}
                data-testid="continue-to-pricing"
              >
                {loading
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>
                  : isPaidUser
                    ? <>Submit answers <ArrowRight className="w-4 h-4" /></>
                    : <>Continue to Pricing <ArrowRight className="w-4 h-4" /></>}
              </button>
            </div>
          )}

          {step === 2 && (
            <div data-testid="pricing-step">
              <div className="flex items-center gap-3 mb-8">
                <span className="w-8 h-8 flex items-center justify-center bg-[#E8A830] text-white text-sm font-bold rounded-full">2</span>
                <span className="text-[#0A1F2E] font-medium">Choose your plan</span>
              </div>

              {/* iter 128 - all 3 Blueprint tiers are one-time payments,
                  so the legacy monthly/annual toggle was removed. Pricing
                  is canonical to Stripe production. */}

              <div className="grid md:grid-cols-3 gap-4 mb-10">
                {tiers.map((t) => {
                  const isOpen = selectedTier === t.key;
                  return (
                    <button
                      key={t.key}
                      onClick={() => handleTierSelect(t.key)}
                      className={`text-left p-6 border transition-all relative rounded-lg ${isOpen ? 'border-[#E8A830] bg-[#E8A830]/5' : 'border-[#d5d0c8] hover:border-[#E8A830]/40'}`}
                      data-testid={`tier-${t.key}`}
                    >
                      {t.popular && <span className="absolute -top-3 left-4 bg-[#E8A830] text-white text-[10px] font-bold px-2 py-0.5 uppercase tracking-wider rounded-full">Most Popular</span>}
                      {t.badge && !t.popular && <span className="absolute -top-3 left-4 bg-[#0A1F2E] text-white text-[10px] font-bold px-2 py-0.5 uppercase tracking-wider rounded-full">{t.badge}</span>}
                      <div className="flex items-baseline gap-1 mb-1">
                        <span className="text-2xl text-[#0A1F2E] font-light">${t.price.toLocaleString()}</span>
                        <span className="text-[#5A6B73] text-sm">{t.interval}</span>
                      </div>
                      <h3 className="text-[#0A1F2E] font-medium">{t.name}</h3>
                      {t.tagline && (
                        <p className="text-[#1A5C6E] text-xs italic mt-1 mb-2" data-testid={`tier-${t.key}-tagline`}>
                          {t.tagline}
                        </p>
                      )}
                      {t.description && (
                        <p
                          className={`text-[#5A6B73] text-xs leading-relaxed mb-3 ${isOpen ? '' : 'md:line-clamp-none line-clamp-3'}`}
                          data-testid={`tier-${t.key}-description`}
                        >
                          {t.description}
                        </p>
                      )}
                      <ul className="space-y-2" data-testid={`tier-${t.key}-bullets`}>
                        {t.features.map((f) => (
                          <li key={f} className="text-[#5A6B73] text-xs flex items-start gap-2">
                            <Check className="w-3 h-3 text-[#E8A830] mt-0.5 shrink-0" /> {f}
                          </li>
                        ))}
                      </ul>
                    </button>
                  );
                })}
              </div>

              <div className="space-y-4 mb-8">
                <div>
                  <label className="block text-[#0A1F2E] font-medium text-sm mb-2">Your full name</label>
                  <input type="text" value={form.name} onChange={(e) => set('name')(e.target.value)} placeholder="Your full name" className="w-full bg-white border border-[#d5d0c8] text-[#0A1F2E] px-4 py-3 text-sm focus:border-[#E8A830] outline-none rounded-lg" data-testid="name-input" />
                </div>
                <div>
                  <label className="block text-[#0A1F2E] font-medium text-sm mb-2">Your email</label>
                  <input type="email" value={form.email} onChange={(e) => set('email')(e.target.value)} onBlur={handleEmailBlur} placeholder="you@example.com" className="w-full bg-white border border-[#d5d0c8] text-[#0A1F2E] px-4 py-3 text-sm focus:border-[#E8A830] outline-none rounded-lg" data-testid="email-input" />
                </div>
              </div>

              <div className="mb-8" data-testid="promo-section">
                <label className="block text-[#0A1F2E] font-medium text-sm mb-2">Have a promo code?</label>
                {promoResult ? (
                  <div className="flex items-center justify-between bg-[#E8A830]/10 border border-[#E8A830]/30 px-4 py-3 rounded-lg" data-testid="promo-applied">
                    <div className="flex items-center gap-2">
                      <Tag className="w-4 h-4 text-[#E8A830]" />
                      <span className="text-[#E8A830] text-sm font-medium">{promoResult.code}</span>
                      <span className="text-[#5A6B73] text-sm">{promoResult.discount_label}</span>
                    </div>
                    <button onClick={clearPromo} className="text-[#5A6B73] hover:text-[#0A1F2E]" data-testid="promo-remove"><X className="w-4 h-4" /></button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input type="text" value={promoCode} onChange={(e) => { setPromoCode(e.target.value); setPromoError(''); }} onKeyDown={(e) => e.key === 'Enter' && handleApplyPromo()} placeholder="Enter code" className="flex-1 bg-white border border-[#d5d0c8] text-[#0A1F2E] px-4 py-3 text-sm focus:border-[#E8A830] outline-none rounded-lg" data-testid="promo-input" />
                    <button onClick={handleApplyPromo} disabled={!promoCode.trim() || promoLoading} className="px-6 py-3 border border-[#d5d0c8] text-[#0A1F2E] text-sm font-medium hover:bg-[#E8A830] hover:text-white rounded-lg disabled:opacity-40" data-testid="promo-apply-btn">{promoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}</button>
                  </div>
                )}
                {promoError && <p className="text-red-500 text-xs mt-2" data-testid="promo-error">{promoError}</p>}
              </div>

              {promoResult && (
                <div className="mb-8 p-4 border border-[#d5d0c8] bg-white rounded-lg" data-testid="order-summary">
                  <div className="flex justify-between text-sm mb-2"><span className="text-[#5A6B73]">{currentTier?.name}</span><span className="text-[#5A6B73] line-through">${currentTier?.price.toLocaleString()}{currentTier?.interval}</span></div>
                  <div className="flex justify-between text-sm mb-2"><span className="text-[#E8A830]">{promoResult.discount_label}</span><span className="text-[#E8A830]">-${promoResult.discount_amount.toFixed(2)}</span></div>
                  <div className="border-t border-[#e0dbd4] pt-2 mt-2 flex justify-between"><span className="text-[#0A1F2E] font-medium text-sm">Total</span><span className="text-[#0A1F2E] font-medium">${promoResult.new_price.toFixed(2)}{currentTier?.interval}</span></div>
                </div>
              )}

              {/* Terms Checkbox - Required */}
              <div className="mb-6 p-4 bg-[#faf9f7] border border-[#e0dbd4] rounded-lg">
                <TermsCheckbox 
                  checked={termsAccepted} 
                  onChange={(e) => setTermsAccepted(e.target.checked)} 
                />
              </div>

              {/* Disclaimer */}
              <p className="text-[#5A6B73] text-xs mb-6 leading-relaxed">
                All AI tools, Blueprint, and checklist are for informational purposes only. Not legal, tax, immigration, or financial advice. Consult qualified professionals. 14-day money-back on Self Serve only.
              </p>

              <div className="flex gap-4">
                <button onClick={() => { setStep(1); window.scrollTo(0, 0); }} className="px-6 py-4 border border-[#d5d0c8] text-[#0A1F2E] text-sm hover:bg-[#0A1F2E] hover:text-white rounded-lg" data-testid="back-button">Back</button>
                <button onClick={handleCheckout} disabled={!form.name || !form.email || !termsAccepted || loading} className={`flex-1 py-4 text-sm font-medium flex items-center justify-center gap-2 rounded-lg ${form.name && form.email && termsAccepted && !loading ? 'bg-[#E8A830] text-white hover:bg-[#a8511f]' : 'bg-[#e0dbd4] text-[#5A6B73] cursor-not-allowed'}`} data-testid="generate-blueprint-btn">
                  {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Processing...</> : <>Get My Blueprint <ArrowRight className="w-4 h-4" /></>}
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
